export const DAY_MAP = {
  'Domingo': 0, 'Lunes': 1, 'Martes': 2, 'Miércoles': 3,
  'Jueves': 4, 'Viernes': 5, 'Sábado': 6
};

// "08:00" → 480
export const timeToMinutes = (timeStr) => {
  if (!timeStr) return 0;
  const [h, m] = timeStr.split(':').map(Number);
  return h * 60 + (m || 0);
};

// 480 → "08:00 AM"
export const minutesToLabel = (minutes) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const ampm = h < 12 ? 'AM' : 'PM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${String(h12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${ampm}`;
};

// 480 → "08:00" (24h, for storage)
export const minutesToTime24 = (minutes) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

export const getDurationMinutes = (duracionEstimada, unidadDuracion) => {
  const val = parseInt(duracionEstimada) || 0;
  if (!val) return 60; // default 1h if not set
  const unit = (unidadDuracion || '').toLowerCase();
  if (unit.includes('hora')) return val * 60;
  if (unit.includes('min')) return val;
  return val * 60; // default treats as hours
};

const DAY_OF_WEEK = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

// Devuelve los minutos de inicio válidos dentro de una franja [startMin, endMin].
// El cliente elige la HORA DE INICIO; la duración del servicio NO recorta la ventana.
// Como en los horarios de camiones: si el proveedor atiende hasta las 8pm, las 8pm es
// una hora de inicio válida (su último servicio del día), sin importar cuánto dure.
const ventanaInicios = (startMin, endMin) => {
  const inicios = [];
  for (let h = startMin; h <= endMin; h += 60) inicios.push(h);
  // El fin de la franja siempre debe ofrecerse como último inicio posible.
  if (inicios[inicios.length - 1] !== endMin) inicios.push(endMin);
  return inicios;
};

// Returns array of { label: "08:00 AM", value: "08:00" } ordenados de más temprano a más tarde.
// El cliente elige la HORA DE INICIO. La medianoche como fin ('00:00') es el último inicio
// del MISMO día (ej. 18:00–00:00 → las 12:00 am son el último servicio de ese día).
// 'fin' de medianoche = 24:00 (1440 min) del propio día, no se arrastra al día siguiente.
const finEnMinutos = (fin) => (fin === '00:00' ? 24 * 60 : timeToMinutes(fin));

export const generateAvailableSlots = (selectedDate, disponibilidad, duracionMinutos, bookedSlots = []) => {
  if (!selectedDate || !disponibilidad || disponibilidad.length === 0) return [];

  // For today, calculate the minimum bookable minute (current time + 30min buffer)
  const now = new Date();
  const isToday = selectedDate.getFullYear() === now.getFullYear() &&
    selectedDate.getMonth() === now.getMonth() &&
    selectedDate.getDate() === now.getDate();
  const minMinute = isToday ? (now.getHours() * 60 + now.getMinutes() + 30) : -1;

  const dayName = DAY_OF_WEEK[selectedDate.getDay()];
  const prevDayName = DAY_OF_WEEK[(selectedDate.getDay() + 6) % 7];

  // Minutos de inicio (0–1439) que caen en el día seleccionado
  const minutosInicio = new Set();

  // 1. Disponibilidad del propio día → inicios hasta la medianoche inclusive (24:00 del mismo día)
  const dayDisp = disponibilidad.find(d => d.day === dayName);
  if (dayDisp?.slots) {
    for (const slot of dayDisp.slots) {
      if (!slot.inicio || !slot.fin) continue;
      const startMin = timeToMinutes(slot.inicio);
      let endMin = finEnMinutos(slot.fin);
      if (endMin <= startMin) endMin += 24 * 60; // franja nocturna heredada (datos viejos)
      if (startMin >= endMin) continue;
      for (const h of ventanaInicios(startMin, endMin)) {
        if (h <= 24 * 60) minutosInicio.add(h); // incluye medianoche (1440); la madrugada (>24:00) es del día siguiente
      }
    }
  }

  // 2. Franja nocturna heredada del día anterior → madrugada (después de medianoche) que arrastra a este día
  const prevDisp = disponibilidad.find(d => d.day === prevDayName);
  if (prevDisp?.slots) {
    for (const slot of prevDisp.slots) {
      if (!slot.inicio || !slot.fin) continue;
      const startMin = timeToMinutes(slot.inicio);
      let endMin = finEnMinutos(slot.fin);
      if (endMin <= startMin) endMin += 24 * 60;
      for (const h of ventanaInicios(startMin, endMin)) {
        if (h > 24 * 60) minutosInicio.add(h - 24 * 60); // solo madrugada real (>medianoche); la medianoche es del día previo
      }
    }
  }

  // 3. Filtrar (buffer de hoy + slots ocupados), ordenar y formatear
  const result = [];
  for (const m of minutosInicio) {
    if (isToday && m < minMinute) continue;
    const isBlocked = bookedSlots.some(b => {
      const bStart = timeToMinutes(b.hora);
      const bEnd = bStart + b.duracionMinutos;
      return m < bEnd && m + duracionMinutos > bStart;
    });
    if (!isBlocked) result.push(m);
  }

  result.sort((a, b) => a - b);
  // La medianoche final (1440) se muestra al último; se etiqueta/guarda como 00:00 (12:00 AM).
  return result.map(m => {
    const mm = m % (24 * 60);
    return { label: minutesToLabel(mm), value: minutesToTime24(mm) };
  });
};

// Returns Set of JS weekday numbers (0-6) that have availability
export const getAvailableWeekdays = (disponibilidad) => {
  if (!disponibilidad || disponibilidad.length === 0) return null; // null = all days available
  const days = new Set();
  for (const d of disponibilidad) {
    const num = DAY_MAP[d.day];
    if (num === undefined || !d.slots) continue;
    const validSlots = d.slots.filter(s => s.inicio && s.fin);
    if (validSlots.length === 0) continue;
    days.add(num);
    // Solo una franja que cruza la medianoche (fin antes del inicio y distinto de 00:00) arrastra
    // madrugada al día siguiente. Un horario que termina justo a medianoche (00:00) es del mismo día.
    const cruzaMedianoche = validSlots.some(s =>
      s.fin !== '00:00' && timeToMinutes(s.fin) <= timeToMinutes(s.inicio)
    );
    if (cruzaMedianoche) days.add((num + 1) % 7);
  }
  return days;
};
